import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@design-system/Skeleton';
import { HealthConnectionsListCard } from '@design-system/Cards/HealthConnectionsListCard';
import { HEALTH_CONNECTIONS_PATH } from '@/constants/paths';
import { useGetMyHealthConnectionsSummary } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { InfoSheet } from './InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';
import { EventTracker } from '@/utils';

interface HealthConnectionsSectionProp {
  isQueryLoading: (isLoading: boolean) => void;
}

export const HealthConnectionsSection = ({
  isQueryLoading,
}: HealthConnectionsSectionProp) => {
  const navigate = useNavigate();
  const {
    data: healthConnectionData,
    isLoading: isHealthConnectionLoading,
    isError: isHealthConnectionError,
  } = useGetMyHealthConnectionsSummary();

  useEffect(() => {
    isQueryLoading(isHealthConnectionLoading);
  }, [isHealthConnectionLoading, isQueryLoading]);

  const handleViewAll = () => {
    EventTracker.trackEvent('health', 'health_HealthConnection_seeAll');
    navigate(`${HEALTH_CONNECTIONS_PATH}`);
  };

  if (isHealthConnectionError || !healthConnectionData?.length) {
    return null;
  }

  if (isHealthConnectionLoading) {
    return (
      <div className="mt-12">
        <Skeleton className="h-[36px] w-[50%] mb-4 rounded-lg" />
        <Skeleton className="h-[20px] w-[80%] mb-4 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              className="h-[100px] w-full rounded-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12" data-testid="health-connections-section">
      <h2 className="dark typography-h1 text-primary mb-2" data-testid="health-connections-title">
        My Health Connections
      </h2>
      <p className="dark typography-body text-secondary mb-6 lg:w-[35rem]" data-testid="health-connections-description">
        Your health scores can reveal important connections to health
        conditions. See if they link to any of the 20+ conditions we assess.
        <InfoSheet
          title={INFO_SHEET_CONTENT.healthConnectionsMain.title}
          description={INFO_SHEET_CONTENT.healthConnectionsMain.description}
        />
      </p>
      <div data-testid="health-connections-card">
        <HealthConnectionsListCard
          connections={healthConnectionData || []}
          onViewAll={handleViewAll}
          className="bg-[#42434D73] p-8 shadow-md"
          onPage="health"
          data-testid="health-connections-listCard"
        />
      </div>
    </div>
  );
};
